/**
 * Custom hook for managing heat pump simulation state and logic
 */

import { useEffect, useRef, useCallback } from "react";
import {
  calculateCOP,
  calculateVolumetricFlow,
  calculateVentilationLoss,
  calculateEnvelopeLoss,
  calculateRadiatorHeatTransfer,
  updateRoomTemperature,
  calculateRoomVolume,
} from "./utils";
import { SIMULATION_UPDATE_INTERVAL, ROOM_HEAT_CAPACITY } from "./constants";

/**
 * Main simulation hook
 * @param {Object} state - Current simulation state
 * @param {Object} setState - State setters
 * @returns {Object} Simulation control functions and computed values
 */
export function useHeatPumpSimulation(state, setState) {
  const timerRef = useRef(null);

  const {
    // System parameters
    outsideTemperature,
    supplyTemperature,
    systemPressure,
    pipeLength,
    pipeDiameter,

    // Room parameters
    roomTemperature,
    targetTemperature,
    uValue,
    totalArea,
    roofArea,
    wallArea,
    airChangeRate,

    // Radiator parameters
    radiatorCount,
    radiatorType,
    valvePosition,
    thermostatOpening,

    // Control parameters
    isAnimationRunning,
    isAnimationPaused,
  } = state;

  /**
   * Calculate total heat losses for the room
   */
  const calculateHeatLosses = useCallback(() => {
    const envelopeLoss = calculateEnvelopeLoss(
      uValue,
      totalArea,
      roomTemperature,
      outsideTemperature,
      roofArea
    );

    const roomVolume = calculateRoomVolume(roofArea, wallArea);
    const ventilationLoss = calculateVentilationLoss(roomTemperature, outsideTemperature, roomVolume, airChangeRate);

    return {
      envelope: envelopeLoss.total,
      ventilation: ventilationLoss,
      total: envelopeLoss.total + ventilationLoss,
      breakdown: envelopeLoss,
    };
  }, [uValue, totalArea, roomTemperature, outsideTemperature, roofArea, wallArea, airChangeRate]);

  /**
   * Calculate losses and flow rate without updating temperature
   * Called whenever parameters change, regardless of animation state
   */
  const calculateStaticValues = useCallback(() => {
    // Calculate volumetric flow
    const volumetricFlow = calculateVolumetricFlow(systemPressure, pipeLength, pipeDiameter);

    // Calculate radiator heat transfer
    const radiatorResult = calculateRadiatorHeatTransfer({
      supplyTemperature,
      roomTemperature,
      volumetricFlow,
      valvePosition,
      thermostatOpening,
      radiatorCount,
      radiatorType,
    });

    // Calculate heat losses
    const heatLossesResult = calculateHeatLosses();
    const { envelope: envelopeLoss, ventilation: ventilationLoss, breakdown } = heatLossesResult;

    // Update only losses and flow-related state (not temperature)
    setState.setReturnTemperature(radiatorResult.returnTemperature);
    setState.setMassFlow(radiatorResult.massFlow);
    setState.setHeatOutput(radiatorResult.heatOutput);
    setState.setEnvelopeLoss(envelopeLoss);
    setState.setVentilationLoss(ventilationLoss);
    setState.setHeatLossBreakdown(breakdown);
  }, [
    systemPressure,
    pipeLength,
    pipeDiameter,
    supplyTemperature,
    roomTemperature,
    valvePosition,
    thermostatOpening,
    radiatorCount,
    radiatorType,
    calculateHeatLosses,
    setState,
  ]);

  /**
   * Perform one simulation step (updates temperature - only when animation is running)
   */
  const simulationStep = useCallback(() => {
    // Calculate volumetric flow
    const volumetricFlow = calculateVolumetricFlow(systemPressure, pipeLength, pipeDiameter);

    // Calculate radiator heat transfer
    const radiatorResult = calculateRadiatorHeatTransfer({
      supplyTemperature,
      roomTemperature,
      volumetricFlow,
      valvePosition,
      thermostatOpening,
      radiatorCount,
      radiatorType,
    });

    // Calculate heat losses
    const heatLossesResult = calculateHeatLosses();
    const { total: totalLoss, envelope: envelopeLoss, ventilation: ventilationLoss, breakdown } = heatLossesResult;

    // Update room temperature (only happens during animation)
    const temperatureResult = updateRoomTemperature({
      currentTemperature: roomTemperature,
      targetTemperature,
      heatInput: radiatorResult.heatOutput,
      heatLoss: totalLoss,
      thermostatOpening,
      roomHeatCapacity: ROOM_HEAT_CAPACITY,
    });

    // Update all state including temperature
    setState.setRoomTemperature(temperatureResult.temperature);
    setState.setThermostatOpening(temperatureResult.thermostatOpening);
    setState.setReturnTemperature(radiatorResult.returnTemperature);
    setState.setMassFlow(radiatorResult.massFlow);
    setState.setHeatOutput(radiatorResult.heatOutput);
    setState.setEnvelopeLoss(envelopeLoss);
    setState.setVentilationLoss(ventilationLoss);
    setState.setHeatLossBreakdown(breakdown);
    setState.setTemperatureChange(temperatureResult.temperatureChange);
  }, [
    systemPressure,
    pipeLength,
    pipeDiameter,
    supplyTemperature,
    roomTemperature,
    valvePosition,
    thermostatOpening,
    radiatorCount,
    radiatorType,
    targetTemperature,
    calculateHeatLosses,
    setState,
  ]);

  /**
   * Recalculate static values whenever parameters change (regardless of animation state)
   */
  useEffect(() => {
    calculateStaticValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    outsideTemperature,
    supplyTemperature,
    systemPressure,
    pipeLength,
    pipeDiameter,
    roomTemperature,
    uValue,
    totalArea,
    roofArea,
    wallArea,
    airChangeRate,
    radiatorCount,
    radiatorType,
    valvePosition,
    thermostatOpening,
  ]);

  /**
   * Start/stop animation timer
   */
  useEffect(() => {
    if (isAnimationRunning && !isAnimationPaused) {
      timerRef.current = setInterval(() => {
        simulationStep();
      }, SIMULATION_UPDATE_INTERVAL);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAnimationRunning, isAnimationPaused, simulationStep]);

  /**
   * Calculate COP
   */
  const cop = calculateCOP(supplyTemperature, outsideTemperature);

  return {
    cop,
    simulationStep,
  };
}

/**
 * Hook for fetching weather data
 * @param {Array} gpsPosition - [latitude, longitude]
 * @param {Function} setOutsideTemperature - State setter for outside temperature
 */
export function useWeatherData(gpsPosition, setOutsideTemperature) {
  useEffect(() => {
    if (!gpsPosition || gpsPosition.length !== 2) return;

    const [latitude, longitude] = gpsPosition;

    const fetchWeather = async () => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m`
        );

        if (!response.ok) {
          console.error("Failed to fetch weather data");
          return;
        }

        const data = await response.json();
        setOutsideTemperature(data.current.temperature_2m);
      } catch (error) {
        console.error("Error fetching weather:", error);
      }
    };

    fetchWeather();
  }, [gpsPosition, setOutsideTemperature]);
}
